import {computed} from 'vue';
import type {FetchError} from 'ofetch';
import {useState} from '#app';
import {useCookie, useLogger, useNuxtApp, useRequestURL, useUniversalFetch, useUniversalFetchRaw} from '#imports';
import {fallbackLng} from '../config';

/**
 * @I18nTour(step = locale_determination_on_the_frontend, includeCode = true)
 *
 * The locale to use is requested to the server via an early GET of /api/locale. But whatever the request
 * made, the API server will store it in a cookie named "i18n", which will be replicated by the Nuxt
 * server.
 *
 * The locale is then exposed to the applications via the <code>locale</code> ref. The language and country code may
 * also be directly accessed using the <code>languageCode</code> and <code>countryCode</code> refs, respectively.
 *
 * Finally, the <code>isMultiLocaleHost</code> ref tells whether the navigation happens on www.malt.com, or on a
 * fully-localized host (xxx.malt.de, xxx.malt.es, xxx.malt.fr, etc.).
 *
 * Typical use:
 *
 * <pre>
 * const {locale, isMultiLocaleHost} = useLocale();
 *
 * doSomethingWith(locale.value);
 *
 * if (isMultiLocaleHost.value) {
 *     // ...
 * }
 * </pre>
 */
export function useLocale() {
    const logger = useLogger();
    const apiCookies = useNuxtApp().$apiCookies;
    const serverSetCookieValues = apiCookies?.cookieValues ?? {};
    const i18nCookie = useCookie<string>('i18n', {readonly: true});

    const url = '/api/locale';
    const requestUrl = useRequestURL();

    const localeState = useState<string | undefined>('i18n', () => serverSetCookieValues?.i18n || i18nCookie.value);

    const locale = computed(() => localeState.value || fallbackLng);
    const splittedLocale = computed(() => locale.value.split('-'));
    const languageCode = computed(() => splittedLocale.value[0]);
    const countryCode = computed(() => splittedLocale.value[1]);
    const isMultiLocaleHost = computed(() => requestUrl.hostname.endsWith('.malt.com'));

    async function _fetchLocale() {
        if (i18nCookie.value) {
            return i18nCookie.value;
        }

        try {
            const response = await useUniversalFetchRaw<string>(url, {
                headers: {
                    Accept: 'text/plain',
                    'Content-Type': 'text/plain',
                },
            });
            if (response.status === 200 && response.headers?.get('Content-Type') === 'application/json' && response._data) {
                return response._data;
            }
            return fallbackLng;
        } catch (err: unknown) {
            const error = err as FetchError;
            logger.error(
                `Error while trying to get locale from ${url}. Will use fallback language instead (${fallbackLng}). Error: message=${error.message}, status=${error.statusCode}`,
            );
            return fallbackLng;
        }
    }

    async function refreshLocale() {
        if (!localeState.value) {
            const newLocale = await _fetchLocale();
            updateLocale(newLocale);
        }
        return localeState.value;
    }

    function updateLocale(newLocale: string) {
        localeState.value = newLocale;
    }

    return {
        countryCode,
        languageCode,
        locale,
        refreshLocale,
        updateLocale,
        isMultiLocaleHost,
    };
}
